import React, { useEffect, useMemo, useState } from "react";
import { CircularProgress, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import ShowChartOutlinedIcon from "@mui/icons-material/ShowChartOutlined";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import MaterialReactTable from "material-react-table";
import { publicApi } from "../../api/http";
import { nhlGoalieColumns, nhlSkaterColumns } from "./Constants";
import PlayerNhlStatsGraph from "./PlayerNhlStatsGraph";

const tableTheme = createTheme({
    palette: {
        mode: "dark",
        info: {
            main: "#7ea862",
            secondary: "#7ea862",
        },
        background: {
            default: "#14181d",
            paper: "#14181d",
        },
        text: {
            primary: "#f3f4f6",
            secondary: "#a1a8b3",
        },
    },
    shadows: ["none"],
});

function parseNhlStatsResponse(data) {
    if (typeof data === "string") {
        try {
            return JSON.parse(data);
        } catch {
            return [];
        }
    }
    return Array.isArray(data) ? data : [];
}

function PlayerNhlCareerStatsTab({ playerId, playerData, isActive }) {
    const [stats, setStats] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [displayMode, setDisplayMode] = useState("table");

    useEffect(() => {
        if (!playerId || !isActive) {
            return;
        }

        let cancelled = false;
        setLoading(true);
        setError(null);

        publicApi
            .get(`/player_nhl_stats/${playerId}`)
            .then((response) => {
                if (cancelled) return;
                setStats(parseNhlStatsResponse(response.data));
            })
            .catch((err) => {
                if (cancelled) return;
                console.error("Error fetching NHL career stats:", err);
                setError("Failed to load NHL career stats.");
                setStats([]);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [playerId, isActive]);

    const isGoalie = useMemo(() => {
        if (playerData?.position === "G") {
            return true;
        }
        if (playerData?.position) {
            return false;
        }
        return stats.some(
            (row) => parseFloat(row.save_percentage) > 0 || parseFloat(row.wins) > 0
        );
    }, [playerData, stats]);

    const columns = useMemo(
        () => (isGoalie ? nhlGoalieColumns : nhlSkaterColumns),
        [isGoalie]
    );

    if (loading) {
        return (
            <div className="player-nhl-stats-loading">
                <CircularProgress size={28} />
            </div>
        );
    }

    if (error) {
        return (
            <Typography variant="body1" component="div" className="empty-tab-message">
                {error}
            </Typography>
        );
    }

    return (
        <>
            <div className="nhl-stats-header">
                <div>
                    <Typography variant="h4" component="div" className="tab-section-title">
                        NHL Career Stats
                    </Typography>
                    <Typography variant="body2" component="div" className="tab-section-subtitle">
                        Career totals synced from the NHL API across all leagues.
                    </Typography>
                </div>
                {stats.length > 0 && (
                    <ToggleButtonGroup
                        value={displayMode}
                        exclusive
                        size="small"
                        onChange={(_, value) => value && setDisplayMode(value)}
                        className="nhl-stats-display-toggle"
                    >
                        <ToggleButton value="table">
                            <TableChartOutlinedIcon fontSize="small" sx={{ mr: 0.5 }} />
                            Table
                        </ToggleButton>
                        <ToggleButton value="graph">
                            <ShowChartOutlinedIcon fontSize="small" sx={{ mr: 0.5 }} />
                            Graph
                        </ToggleButton>
                    </ToggleButtonGroup>
                )}
            </div>

            {stats.length > 0 ? (
                displayMode === "graph" ? (
                    <PlayerNhlStatsGraph stats={stats} isGoalie={isGoalie} playerId={playerId} />
                ) : (
                    <ThemeProvider theme={tableTheme}>
                        <MaterialReactTable
                            columns={columns}
                            data={stats}
                            initialState={{ density: "compact", pagination: { pageSize: 15 } }}
                        />
                    </ThemeProvider>
                )
            ) : (
                <Typography variant="body1" component="div" className="no-stats-message">
                    No NHL career stats synced for this player yet.
                </Typography>
            )}
        </>
    );
}

export default PlayerNhlCareerStatsTab;
